import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import { cachedGetOr } from '../utils/api';

export default function Dashboard() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [games, setGames] = useState([]);
  const [activeGames, setActiveGames] = useState([]);
  const [recentSessions, setRecentSessions] = useState([]);
  const [activity, setActivity] = useState([]);
  const [stats, setStats] = useState({});
  const [gameHistory, setGameHistory] = useState([]);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [socket, setSocket] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showingCached, setShowingCached] = useState(false);

  useEffect(() => {
    // Connect to Socket.io
    const newSocket = io(window.API_URL, {
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      newSocket.emit('user-online', { token });
    });

    newSocket.on('online-users', (users) => {
      setOnlineUsers(users);
    });

    setSocket(newSocket);

    // Fetch all data
    fetchData();

    return () => {
      newSocket.disconnect();
    };
  }, [token]);

  const fetchData = async () => {
    // Admin sees every active game, everyone else only their own
    const isUserAdmin = user?.username === 'jaydenmetz' || user?.is_admin;
    const activeEndpoint = isUserAdmin
      ? '/api/games/active/all'
      : `/api/games/active/user/${user?.id}`;

    // These reads are independent, so they run in parallel and each falls back
    // to its own cached copy. They used to be sequential inside a single try
    // block, which meant the first failure blanked the whole dashboard.
    const [games, users, active, recent, recentActivity, siteStats, history, online] =
      await Promise.all([
        cachedGetOr('/api/games', []),
        cachedGetOr('/api/users', []),
        cachedGetOr(activeEndpoint, []),
        cachedGetOr('/api/scores/recent', []),
        cachedGetOr('/api/scores/activity?limit=15', []),
        cachedGetOr('/api/scores/stats', {}),
        cachedGetOr('/api/scores/history', []),
        cachedGetOr('/api/online-users', [])
      ]);

    setGames(games.data || []);
    setAllUsers(users.data || []);
    setActiveGames(active.data || []);
    setRecentSessions((recent.data || []).slice(0, 10));
    setActivity(recentActivity.data || []);
    setStats(siteStats.data || {});
    setGameHistory(history.data || []);
    setOnlineUsers(online.data || []);

    // Any stale read means we're showing last-known data from the device
    setShowingCached(
      [games, users, active, recent, recentActivity, siteStats, history, online]
        .some((r) => r.stale)
    );
  };

  const handleJoinGame = async (e) => {
    e.preventDefault();
    if (!joinCode.trim()) return;
    // Navigate to join page which handles the logic
    navigate(`/join/${joinCode.toUpperCase()}`);
  };

  // Check if user is admin
  const isAdmin = user?.username === 'jaydenmetz' || user?.is_admin;

  // Filter active games - admin sees all, others see only their games
  const visibleActiveGames = isAdmin
    ? activeGames
    : activeGames.filter(game =>
        game.players?.some(p => p.user_id === user?.id) || game.host_user_id === user?.id
      );

  // Separate games by status
  const setupGames = visibleActiveGames.filter(g => g.status === 'setup');
  const playingGames = visibleActiveGames.filter(g => g.status === 'playing');
  const currentGames = [...setupGames, ...playingGames];

  const handleStartGame = async (gameSlug) => {
    navigate(`/game-setup/${gameSlug}`);
  };

  const handleResumeGame = (activeGameId) => {
    navigate(`/game/${activeGameId}`);
  };

  const handleCancelGame = async (activeGameId, keepStats) => {
    try {
      if (keepStats) {
        // End game without keeping stats (partial game)
        await fetch(`${window.API_URL}/api/games/active/${activeGameId}/end`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ keep_stats: false })
        });
      } else {
        // Just delete the game
        await fetch(`${window.API_URL}/api/games/active/${activeGameId}`, {
          method: 'DELETE'
        });
      }
      setShowCancelModal(null);
      fetchData();
    } catch (error) {
      console.error('Error cancelling game:', error);
    }
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const then = new Date(date);
    const diff = Math.floor((now - then) / 1000);

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return then.toLocaleDateString();
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'win': return '🏆';
      case 'game_complete': return '🎮';
      case 'game_setup': return '🎲';
      case 'game_start': return '▶️';
      case 'streak': return '🔥';
      case 'comeback': return '📈';
      case 'milestone': return '🎯';
      default: return '📋';
    }
  };

  const getActivityText = (item) => {
    switch (item.activity_type) {
      case 'win':
        return <><strong className="text-green-400">{item.user_name}</strong> won with {item.metadata?.score} pts</>;
      case 'game_complete':
        return <>{item.metadata?.player_count} players finished a game</>;
      case 'game_setup':
        return <><strong className="text-blue-400">{item.user_name}</strong> started setting up a game</>;
      case 'game_start':
        return <><strong className="text-purple-400">{item.user_name}</strong> started a game with {item.metadata?.player_count} players</>;
      case 'streak':
        return <><strong className="text-orange-400">{item.user_name}</strong> is on a {item.metadata?.streak}-game win streak!</>;
      case 'comeback':
        return <><strong className="text-green-400">{item.user_name}</strong> made an incredible comeback!</>;
      case 'milestone':
        return <><strong className="text-yellow-400">{item.user_name}</strong> earned {item.metadata?.milestone_emoji} {item.metadata?.milestone_name}!</>;
      default:
        return <>{item.user_name} did something</>;
    }
  };

  // Sort users: online first, then by last_seen
  const sortedUsers = [...allUsers].sort((a, b) => {
    const aOnline = onlineUsers.some(u => u.userId === a.id);
    const bOnline = onlineUsers.some(u => u.userId === b.id);
    if (aOnline && !bOnline) return -1;
    if (!aOnline && bOnline) return 1;
    return new Date(b.last_seen || 0) - new Date(a.last_seen || 0);
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 p-4">
      <div className="max-w-6xl mx-auto">
        {showingCached && (
          <div className="mb-4 rounded-lg bg-slate-700/70 text-slate-200 text-xs text-center py-1.5 px-3">
            Showing saved data from this device — the server is unreachable.
          </div>
        )}
        {/* Header */}
        <header className="relative mb-6">
          <div className="text-center">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
              🎮 Games
            </h1>
          </div>

          {/* Profile Circle */}
          <div className="absolute top-0 right-0">
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white shadow-lg hover:scale-105 transition-transform ring-2 ring-white/20 hover:ring-white/40"
              style={{ backgroundColor: user?.avatar_color || '#6366f1' }}
            >
              {user?.name?.charAt(0).toUpperCase()}
            </button>

            {/* Dropdown Menu */}
            {showProfileMenu && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowProfileMenu(false)}
                />
                <div className="absolute right-0 mt-2 w-64 bg-slate-800 rounded-xl shadow-xl border border-white/10 overflow-hidden z-50">
                  {/* User Info */}
                  <div className="p-4 bg-gradient-to-r from-slate-700 to-slate-800 border-b border-white/10">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg text-white"
                        style={{ backgroundColor: user?.avatar_color || '#6366f1' }}
                      >
                        {user?.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-white font-semibold">{user?.name}</div>
                        <div className="text-slate-400 text-sm">@{user?.username}</div>
                      </div>
                    </div>
                  </div>

                  {/* Menu Items */}
                  <div className="py-2">
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        navigate(`/profile/${user?.id}`);
                      }}
                      className="w-full px-4 py-2.5 text-left text-slate-200 hover:bg-white/10 flex items-center gap-3 transition-colors"
                    >
                      <span className="text-lg">📊</span>
                      <span>My Stats</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        navigate('/achievements');
                      }}
                      className="w-full px-4 py-2.5 text-left text-slate-200 hover:bg-white/10 flex items-center gap-3 transition-colors"
                    >
                      <span className="text-lg">🏆</span>
                      <span>Achievements</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        navigate('/leaderboards');
                      }}
                      className="w-full px-4 py-2.5 text-left text-slate-200 hover:bg-white/10 flex items-center gap-3 transition-colors"
                    >
                      <span className="text-lg">🥇</span>
                      <span>Leaderboards</span>
                    </button>
                  </div>

                  {/* Sign Out */}
                  <div className="border-t border-white/10 py-2">
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        logout();
                      }}
                      className="w-full px-4 py-2.5 text-left text-red-400 hover:bg-red-500/10 flex items-center gap-3 transition-colors"
                    >
                      <span className="text-lg">🚪</span>
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </header>

        {/* Players Section */}
        <div className="mb-6">
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            👥 Players
          </h2>
          <div className="flex gap-3 flex-wrap">
            {sortedUsers.map(u => {
              const isOnline = onlineUsers.some(ou => ou.userId === u.id);
              const isMe = u.id === user?.id;
              return (
                <div
                  key={u.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-full border transition-all cursor-pointer hover:bg-white/5
                    ${isOnline
                      ? 'border-green-500 bg-green-500/10 shadow-lg shadow-green-500/20'
                      : 'border-white/10 bg-white/5'
                    }
                    ${isMe ? 'ring-2 ring-blue-500' : ''}
                  `}
                  onClick={() => navigate(`/profile/${u.id}`)}
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm relative"
                    style={{ backgroundColor: u.avatar_color || '#6366f1' }}
                  >
                    {u.name?.charAt(0).toUpperCase()}
                    {isOnline && (
                      <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-slate-900" />
                    )}
                  </div>
                  <div>
                    <div className="text-white text-sm font-medium">
                      {u.name?.split(' ')[0]}
                      {isMe && <span className="text-slate-400 text-xs ml-1">(you)</span>}
                    </div>
                    <div className={`text-xs ${isOnline ? 'text-green-400' : 'text-slate-500'}`}>
                      {isOnline ? 'Online now' : u.last_seen ? `Last seen ${formatTimeAgo(u.last_seen)}` : 'Never played'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Current Games (Setup + Playing) */}
        {currentGames.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              🎮 Current Games
              {isAdmin && <span className="text-xs text-blue-400 font-normal">(Admin View)</span>}
            </h2>
            <div className="space-y-3">
              {currentGames.map(game => (
                <div
                  key={game.id}
                  className={`rounded-xl p-4 flex items-center gap-4 ${
                    game.status === 'setup'
                      ? 'bg-blue-500/10 border border-blue-500/30'
                      : 'bg-amber-500/10 border border-amber-500/30'
                  }`}
                >
                  <span className="text-3xl">{game.emoji}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-semibold">{game.game_name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        game.status === 'setup'
                          ? 'bg-blue-500/20 text-blue-300'
                          : 'bg-amber-500/20 text-amber-300'
                      }`}>
                        {game.status === 'setup' ? 'Setting Up' : 'In Progress'}
                      </span>
                    </div>
                    <div className="text-slate-400 text-sm mt-1">
                      {game.players?.length || 0} players • Code: <span className="font-mono text-white">{game.join_code}</span>
                    </div>
                    <div className="flex gap-1 mt-2">
                      {game.players?.slice(0, 5).map((p, i) => (
                        <div
                          key={i}
                          className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold"
                          style={{ backgroundColor: p.avatar_color || '#6366f1' }}
                          title={p.name}
                        >
                          {p.name?.charAt(0)}
                        </div>
                      ))}
                      {(game.players?.length || 0) > 5 && (
                        <div className="w-6 h-6 rounded-full bg-slate-600 flex items-center justify-center text-xs">
                          +{game.players.length - 5}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => game.status === 'setup' ? navigate(`/game/${game.id}`) : handleResumeGame(game.id)}
                      className={`font-medium px-4 py-2 rounded-lg text-sm ${
                        game.status === 'setup'
                          ? 'bg-blue-600 hover:bg-blue-500 text-white'
                          : 'bg-amber-500 hover:bg-amber-400 text-black'
                      }`}
                    >
                      {game.status === 'setup' ? 'Join Setup' : 'Resume'}
                    </button>
                    <button
                      onClick={() => setShowCancelModal(game.id)}
                      className="text-slate-400 hover:text-red-400 p-2"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Join Game */}
        <div className="bg-slate-800/50 rounded-xl p-4 mb-6">
          <h2 className="text-sm font-semibold text-white mb-3">Join a Game</h2>
          <form onSubmit={handleJoinGame} className="flex gap-3">
            <input
              type="text"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="Enter game code"
              maxLength={6}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="characters"
              spellCheck="false"
              className="flex-1 bg-slate-700 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase tracking-widest text-center text-lg font-mono"
            />
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-500 text-white font-medium px-6 py-3 rounded-lg transition-colors"
            >
              Join
            </button>
          </form>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Games */}
          <div className="lg:col-span-2 space-y-6">
            {/* Games Grid */}
            <div>
              <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                🎯 Choose Your Game
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {games.map(game => (
                  <div
                    key={game.id}
                    className={`bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-5 border-l-4 transition-all hover:scale-[1.02] hover:shadow-xl cursor-pointer
                      ${game.slug === 'five-crowns' ? 'border-emerald-500' :
                        game.slug === 'phase-10' ? 'border-cyan-500' : 'border-slate-500'
                      }`}
                    onClick={() => handleStartGame(game.slug)}
                  >
                    <div className="text-3xl mb-3">{game.emoji || '🎮'}</div>
                    <div className="text-xl font-bold text-white mb-3">{game.name}</div>

                    <div className="flex justify-between mb-4 pb-4 border-b border-white/10">
                      <div className="text-center">
                        <div className="text-lg font-bold text-white">{game.total_games || 0}</div>
                        <div className="text-xs text-slate-400 uppercase">Games Played</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-white">{game.best_score || '—'}</div>
                        <div className="text-xs text-slate-400 uppercase">Best Score</div>
                      </div>
                    </div>

                    {game.last_winner && (
                      <div className="flex items-center gap-2 mb-4">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                          style={{ background: 'linear-gradient(135deg, #f5af19, #f12711)' }}
                        >
                          {game.last_winner.name?.charAt(0)}
                        </div>
                        <div>
                          <div className="text-xs text-slate-400 uppercase">Last Winner</div>
                          <div className="text-white font-medium text-sm">
                            {game.last_winner.name} • {game.last_winner.score} pts
                          </div>
                        </div>
                      </div>
                    )}

                    <button
                      className={`w-full py-2.5 rounded-lg font-bold text-sm transition-all
                        ${game.slug === 'five-crowns' ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-black hover:from-emerald-400' :
                          game.slug === 'phase-10' ? 'bg-gradient-to-r from-cyan-500 to-cyan-600 text-black hover:from-cyan-400' :
                          'bg-slate-600 text-white'
                        }`}
                    >
                      Play {game.name}
                    </button>
                  </div>
                ))}

                {/* Add New Game Placeholder */}
                <div className="bg-slate-800/30 rounded-xl p-5 border-l-4 border-slate-600 opacity-50">
                  <div className="text-3xl mb-3">➕</div>
                  <div className="text-xl font-bold text-white mb-3">Add New Game</div>
                  <div className="flex justify-between mb-4 pb-4 border-b border-white/10">
                    <div className="text-center">
                      <div className="text-lg font-bold text-white">—</div>
                      <div className="text-xs text-slate-400 uppercase">Games Played</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-white">—</div>
                      <div className="text-xs text-slate-400 uppercase">Best Score</div>
                    </div>
                  </div>
                  <button className="w-full py-2.5 rounded-lg font-bold text-sm bg-slate-700 text-slate-400 cursor-not-allowed">
                    Coming Soon
                  </button>
                </div>
              </div>
            </div>

            {/* Stats Ticker */}
            <div className="bg-white/5 rounded-xl p-4 flex flex-wrap gap-6 justify-center">
              {stats.longest_streak && (
                <div className="flex items-center gap-2 text-sm">
                  <span>🔥</span>
                  <span className="text-slate-300">Longest Streak:</span>
                  <span className="text-orange-400 font-bold">{stats.longest_streak.name} ({stats.longest_streak.streak} games)</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm">
                <span>📊</span>
                <span className="text-slate-300">Total Points Scored:</span>
                <span className="text-orange-400 font-bold">{stats.total_points?.toLocaleString() || 0}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span>🎲</span>
                <span className="text-slate-300">Total Games:</span>
                <span className="text-orange-400 font-bold">{stats.total_games || 0}</span>
              </div>
              {stats.comeback_king && (
                <div className="flex items-center gap-2 text-sm">
                  <span>⚡</span>
                  <span className="text-slate-300">Comeback King:</span>
                  <span className="text-orange-400 font-bold">{stats.comeback_king.name}</span>
                </div>
              )}
            </div>

            {/* Game History */}
            {gameHistory.length > 0 && (
              <div className="bg-slate-800/50 rounded-xl overflow-hidden">
                <div className="p-4 border-b border-white/10 flex justify-between items-center">
                  <h2 className="font-semibold text-white flex items-center gap-2">
                    <span>📜</span> Game History
                  </h2>
                  <button
                    onClick={() => setShowAllHistory(!showAllHistory)}
                    className="text-xs text-slate-400 bg-white/10 px-3 py-1 rounded-full hover:bg-white/20"
                  >
                    {showAllHistory ? 'Show Less' : `Show All (${gameHistory.length})`}
                  </button>
                </div>
                <div className="divide-y divide-white/5">
                  {(showAllHistory ? gameHistory : gameHistory.slice(0, 5)).map((game) => (
                    <div
                      key={game.session_id}
                      id={`history-${game.session_id}`}
                      className="p-4 hover:bg-white/5 transition-colors cursor-pointer"
                      onClick={() => navigate(`/history/${game.session_id}`)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl
                          ${game.slug === 'five-crowns' ? 'bg-emerald-500/20' :
                            game.slug === 'phase-10' ? 'bg-cyan-500/20' : 'bg-purple-500/20'
                          }`}>
                          {game.emoji || '🎮'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-xs px-2 py-0.5 rounded-full
                              ${game.slug === 'five-crowns' ? 'bg-emerald-500/20 text-emerald-400' :
                                game.slug === 'phase-10' ? 'bg-cyan-500/20 text-cyan-400' : 'bg-purple-500/20 text-purple-400'
                              }`}>
                              {game.game_name}
                            </span>
                            <span className="text-xs text-slate-500">
                              {formatTimeAgo(game.played_at)}
                            </span>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {game.players?.map((player, idx) => (
                              <div
                                key={player.user_id}
                                className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-sm
                                  ${idx === 0 ? 'bg-yellow-500/20 text-yellow-300' : 'bg-white/5 text-slate-300'
                                }`}
                              >
                                {idx === 0 && <span className="text-xs">🏆</span>}
                                <div
                                  className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold"
                                  style={{ backgroundColor: player.avatar_color || '#6366f1' }}
                                >
                                  {player.name?.charAt(0)}
                                </div>
                                <span className="font-medium">{player.name?.split(' ')[0]}</span>
                                <span className="text-slate-400">{player.score}</span>
                              </div>
                            ))}
                          </div>
                          <div className="text-xs text-blue-400 mt-2">View full scorecard →</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Activity Feed */}
          <div className="bg-slate-800/50 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-white/10 flex justify-between items-center">
              <h2 className="font-semibold text-white">📜 Recent Activity</h2>
              <button className="text-xs text-slate-400 bg-white/10 px-3 py-1 rounded-full hover:bg-white/20">
                All Games ▾
              </button>
            </div>
            <div className="max-h-[500px] overflow-y-auto">
              {/* Online users notification */}
              {onlineUsers.length > 0 && (
                <div className="p-4 border-b border-white/5 flex gap-3 hover:bg-white/5">
                  <div className="w-9 h-9 rounded-lg bg-purple-500/20 flex items-center justify-center">👋</div>
                  <div className="flex-1">
                    <div className="text-sm text-slate-200">
                      <strong className="text-green-400">
                        {onlineUsers.map(u => u.name?.split(' ')[0]).join(' and ')}
                      </strong> {onlineUsers.length === 1 ? 'is' : 'are'} online
                    </div>
                    <div className="text-xs text-slate-500">Just now</div>
                  </div>
                </div>
              )}

              {activity
                // Filter out game_complete if there's a matching win activity (avoid duplicates)
                .filter((item, idx, arr) => {
                  if (item.activity_type === 'game_complete') {
                    // Check if there's a win activity for the same session
                    const hasWinForSession = arr.some(
                      other => other.activity_type === 'win' && other.session_id === item.session_id
                    );
                    return !hasWinForSession;
                  }
                  return true;
                })
                .map((item, i) => (
                <div
                  key={i}
                  className={`p-4 border-b border-white/5 flex gap-3 hover:bg-white/5 transition-colors ${
                    (item.activity_type === 'win' || item.activity_type === 'game_complete') && item.session_id
                      ? 'cursor-pointer'
                      : ''
                  }`}
                  onClick={() => {
                    if ((item.activity_type === 'win' || item.activity_type === 'game_complete') && item.session_id) {
                      // Find the game in history and scroll to it, or show modal
                      const game = gameHistory.find(g => g.session_id === item.session_id);
                      if (game) {
                        setShowAllHistory(true);
                        // Scroll to game history section
                        setTimeout(() => {
                          document.getElementById(`history-${game.session_id}`)?.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      }
                    }
                  }}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-lg
                    ${item.activity_type === 'win' ? 'bg-green-500/20' :
                      item.activity_type === 'game_complete' ? 'bg-indigo-500/20' :
                      item.activity_type === 'streak' ? 'bg-orange-500/20' : 'bg-purple-500/20'
                    }`}>
                    {getActivityIcon(item.activity_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-200 leading-relaxed">
                      {getActivityText(item)}
                      {item.game_name && (
                        <span className={`ml-2 text-xs px-1.5 py-0.5 rounded inline-block
                          ${item.game_slug === 'five-crowns' ? 'bg-emerald-500/20 text-emerald-400' :
                            item.game_slug === 'phase-10' ? 'bg-cyan-500/20 text-cyan-400' :
                            'bg-purple-500/20 text-purple-400'
                          }`}>
                          {item.game_name}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {formatTimeAgo(item.created_at)}
                      {(item.activity_type === 'win' || item.activity_type === 'game_complete') && item.session_id && (
                        <span className="ml-2 text-blue-400">View scorecard →</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>

      {/* Cancel Game Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 rounded-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-white mb-2">Cancel Game?</h3>
            <p className="text-slate-400 text-sm mb-4">
              What would you like to do with this unfinished game?
            </p>
            <div className="space-y-2">
              <button
                onClick={() => handleCancelGame(showCancelModal, false)}
                className="w-full bg-red-600 hover:bg-red-500 text-white py-2.5 rounded-lg font-medium"
              >
                Delete Game (Don't Save)
              </button>
              <button
                onClick={() => setShowCancelModal(null)}
                className="w-full bg-slate-700 hover:bg-slate-600 text-white py-2.5 rounded-lg font-medium"
              >
                Keep Playing
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
